import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "../client";
import {
  CostumeCreate,
  CostumeRead,
  CostumeUpdate,
  type CostumeCreate as CostumeCreateT,
  type CostumeUpdate as CostumeUpdateT,
} from "../schemas";
import { qk } from "../queryKeys";

const CostumeList = z.array(CostumeRead);

export function useCostumes(projectId: number) {
  return useQuery({
    queryKey: qk.costumes(projectId),
    queryFn: () => api.get(`/api/projects/${projectId}/costumes`, CostumeList),
    enabled: projectId > 0,
  });
}

export function useCreateCostume(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CostumeCreateT) =>
      api.post(
        `/api/projects/${projectId}/costumes`,
        CostumeCreate.parse(input),
        CostumeRead,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.costumes(projectId) }),
  });
}

export function useUpdateCostume(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: CostumeUpdateT }) =>
      api.patch(`/api/costumes/${id}`, CostumeUpdate.parse(patch), CostumeRead),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.costumes(projectId) }),
  });
}

export function useDeleteCostume(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/costumes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.costumes(projectId) }),
  });
}
